import React, { useState, useEffect, useLayoutEffect, useCallback, useRef, useMemo, useDeferredValue } from 'react';
import { pinnedLeftOffsets, prunePinned } from '../../utils/gridPin';
import { buildCatalogQuery, nextSort, addFilter, isGlobalView } from '../../utils/catalogQuery';
import { useTranslation } from 'react-i18next';
import ConnTabsWindow, { ConnTabPane } from './ConnTabsWindow';
import memoWindow from '../common/memoWindow';
import OwnConnTarget from './OwnConnTarget';
import useOwnConnSession, { ORACLE_OR_PG_CONN } from '../../hooks/useOwnConnSession';
import { formatApiError, formatTime } from '../../utils/formatters';
import axios from 'axios';
import { FiX, FiRefreshCw, FiPlay, FiInfo, FiStar, FiGrid, FiSearch, FiDatabase } from 'react-icons/fi';
import './DynamicViewExplorerModal.css';

import { menuPoint } from '../../utils/appZoom';
// 동적뷰 즐겨찾기 최대 개수 — 탭이 5개를 넘으면 한 줄에 다 안 들어와 잘린다.
const MAX_FAVORITE_VIEWS = 5;
// dbType 별 즐겨찾기를 따로 저장 — Oracle 뷰 목록과 Postgres 뷰 목록은 겹치지 않는다.
const favoritesKey = (dbType) => `livesql_dynview_fav_${dbType}`;

// 손으로 고른 목록이 아니라 **이 연결이 실제로 갖고 있는 딕셔너리/동적뷰 전체**를 그대로 보여준다
// (연결 계정 권한에 따라 결과가 달라질 수 있음 — 없으면 목록에 안 뜨거나 조회 시 권한 에러).
//   Oracle     : DICTIONARY(딕셔너리 뷰, comments 포함) + V$FIXED_TABLE(V$/GV$ 동적성능뷰)
//   PostgreSQL : pg_catalog.pg_views(시스템 카탈로그/통계 뷰) + information_schema.views
const CATALOG_QUERY_BY_DBTYPE = {
  /* eslint-disable-next-line no-restricted-syntax -- 카탈로그 SQL 이 kind 컬럼으로 주는 값. 표시명은 kindLabel() 에서 번역한다 */
  oracle: `SELECT view_name AS name, kind, description FROM (
             SELECT table_name AS view_name, '딕셔너리' AS kind, comments AS description
               FROM dictionary
              WHERE table_name LIKE 'DBA_%' OR table_name LIKE 'ALL_%' OR table_name LIKE 'USER_%'
             UNION ALL
             SELECT name AS view_name, '동적뷰(V$)' AS kind, CAST(NULL AS VARCHAR2(4000)) AS description
               FROM v$fixed_table
              WHERE name LIKE 'V$%' OR name LIKE 'GV$%'
           )
           ORDER BY kind, view_name
           FETCH FIRST 2000 ROWS ONLY`,
  /* eslint-disable-next-line no-restricted-syntax -- 카탈로그 SQL 이 kind 컬럼으로 주는 값. 표시명은 kindLabel() 에서 번역한다 */
  postgresql: `SELECT name, kind, NULL::text AS description FROM (
             SELECT viewname AS name,
                    CASE WHEN viewname LIKE 'pg_stat%' OR viewname LIKE 'pg_stio%' THEN '동적뷰' ELSE '딕셔너리' END AS kind
               FROM pg_catalog.pg_views
              WHERE schemaname = 'pg_catalog'
             UNION ALL
             SELECT table_name AS name, '딕셔너리(information_schema)' AS kind
               FROM information_schema.views
              WHERE table_schema = 'information_schema'
           ) x
           ORDER BY kind, name
           LIMIT 2000`,
};

// 카탈로그가 안 뜬 상태(처음 로딩 전)에도 즐겨찾기 기본값과 자주 쓰는 뷰의 설명을 보여줄 수 있도록
// 자주 쓰는 항목만 손으로 정리한 보조 설명 — 딕셔너리 comments 가 없는 V$/pg_stat 계열 보완용.
const getCuratedDescriptions = (t) => ({
  'V$ACTIVE_SESSION_HISTORY': t('monitoring:dv.desc.ash'),
  'V$SQL_MONITOR': t('monitoring:dv.desc.sqlMonitor'),
  'V$SQL_WORKAREA_ACTIVE': t('monitoring:dv.desc.workarea'),
  'V$SESSION': t('monitoring:dv.desc.sessions'),
  'V$SQLAREA': t('monitoring:dv.desc.topBufferGets'),
  'V$SYSTEM_EVENT': t('monitoring:dv.desc.systemEvent'),
  'V$LOCK': t('monitoring:dv.desc.locks'),
  'V$OPEN_CURSOR': t('monitoring:dv.desc.openCursors'),
  'V$PARAMETER': t('monitoring:dv.desc.parameter'),
  'V$SGASTAT': t('monitoring:dv.desc.sgastat'),
  'V$PROCESS': t('monitoring:dv.desc.process'),
  'V$INSTANCE': t('monitoring:dv.desc.instance'),
  'V$DATABASE': t('monitoring:dv.desc.database'),
  'V$TABLESPACE': t('monitoring:dv.desc.tablespace'),
  'V$DATAFILE': t('monitoring:dv.desc.datafile'),
  'V$LOG': t('monitoring:dv.desc.log'),
  'V$LOGFILE': t('monitoring:dv.desc.logfile'),
  'V$UNDOSTAT': t('monitoring:dv.desc.undostat'),
  'V$SQL': t('monitoring:dv.desc.sql'),
  'V$SQLTEXT': t('monitoring:dv.desc.sqltext'),
  'V$LIBRARYCACHE': t('monitoring:dv.desc.librarycache'),
  'V$ROWCACHE': t('monitoring:dv.desc.rowcache'),
  'V$SYSSTAT': t('monitoring:dv.desc.sysstat'),
  'V$SESSTAT': t('monitoring:dv.desc.sesstat'),
  'V$EVENT_NAME': t('monitoring:dv.desc.eventName'),
  'PG_STAT_ACTIVITY': t('monitoring:dv.desc.activity'),
  'PG_STAT_STATEMENTS': t('monitoring:dv.desc.pgStatStatements'),
  'PG_LOCKS': t('monitoring:dv.desc.locks'),
  'PG_STAT_USER_TABLES': t('monitoring:dv.desc.pgStatTables'),
  'PG_STAT_USER_INDEXES': t('monitoring:dv.desc.pgStatIndexes'),
  'PG_STAT_DATABASE': t('monitoring:dv.desc.pgStatDatabase'),
  'PG_STAT_BGWRITER': t('monitoring:dv.desc.pgBgwriter'),
  'PG_SETTINGS': t('monitoring:dv.desc.pgSettings'),
  'PG_STAT_REPLICATION': t('monitoring:dv.desc.pgReplication'),
});

// 위 목록에 없는 V$/GV$ 뷰는 최소한 "어떤 종류인지"라도 보여준다 — 완전히 빈 것보다 낫다.
// (딕셔너리 뷰는 보통 DICTIONARY.comments 가 있어 이 fallback 까지 오는 경우가 적다.)
function genericDescription(name, t) {
  if (/^GV\$/.test(name)) return t('monitoring:dv.desc.genericGv');
  if (/^V\$/.test(name)) return t('monitoring:dv.desc.genericV');
  return null;
}

// 첫 방문(즐겨찾기 없음) 기본값 — 기존 3탭 구성과 동일해 마이그레이션 체감이 없게 한다.
// 대소문자는 각 DBMS 카탈로그가 실제로 돌려주는 표기를 그대로 따른다(Oracle=대문자, PG=소문자).
// 예전 기본값(V$ 3종)을 **그대로 쓰고 있던 사람만** 새 기본값(GV$)으로 옮긴다.
// 기본값만 바꾸면 이미 저장된 사람에게는 아무 일도 일어나지 않는다 — 정작 요청한 사람이
// 그 경우다. 반대로 직접 고른 목록까지 건드리면 남의 설정을 지우는 것이라,
// **옛 기본값과 정확히 같을 때만** 바꾼다.
const LEGACY_ORACLE_FAVORITES = ['V$ACTIVE_SESSION_HISTORY', 'V$SQL_MONITOR', 'V$SESSION'];
function migrateLegacyFavorites(dbType, list) {
  if (dbType !== 'oracle') return list;
  const same = list.length === LEGACY_ORACLE_FAVORITES.length
    && list.every((v, i) => v === LEGACY_ORACLE_FAVORITES[i]);
  return same ? DEFAULT_FAVORITES.oracle : list;
}

const DEFAULT_FAVORITES = {
  // RAC 에서 한 노드만 보고 있으면 "왜 안 보이지" 로 끝난다 — 기본은 전체 노드(GV$)다.
  // 노드를 좁히는 것은 인스턴스 리본으로 언제든 할 수 있다.
  oracle: ['GV$ACTIVE_SESSION_HISTORY', 'GV$SQL_MONITOR', 'GV$SESSION'],
  postgresql: ['pg_stat_activity', 'pg_locks', 'pg_stat_user_tables'],
};

// 카탈로그가 돌려준 이름을 그대로 FROM 절에 꽂는다 — DB 자체 메타데이터라 신뢰하되,
// 식별자 형태를 벗어나면(공백/따옴표 등) 방어적으로 거른다.
const IDENT_RE = /^[A-Za-z_][A-Za-z0-9_$#.]*$/;
// 예전엔 여기서 FETCH FIRST 200/LIMIT 200 을 SQL 에 직접 박아뒀는데, 그러면 커서를 아무리
// 더 당겨도(다음 버튼) DB 쪽 결과집합 자체가 이미 200건으로 잘려 있어 "다음"이 불가능했다.
// 행 개수 제한은 이제 SQL 이 아니라 /fetch-data 의 fetchSize(배치 크기)가 담당한다.
function buildDefaultQuery(dbType, viewName, t) {
  if (!IDENT_RE.test(viewName)) return t('monitoring:dv.invalidViewName', { name: viewName });
  return `SELECT * FROM ${viewName}`;
}

// 컬럼명으로도 검색할 수 있게 카탈로그 전체의 (뷰 이름 → 컬럼 목록) 을 한 번에 가져온다.
// Oracle: DBA_/ALL_/USER_ 딕셔너리 뷰는 ALL_TAB_COLUMNS 에 그대로 있고, V$/GV$ 는 내부적으로
// V_$xxx / GV_$xxx (밑줄 포함) 로 저장돼 있어 그 형태로 조회한 뒤 밑줄을 떼어 공개 이름으로 되돌린다.
const COLUMN_QUERY_BY_DBTYPE = {
  oracle: `SELECT table_name, column_name FROM all_tab_columns
            WHERE owner = 'SYS' AND (table_name LIKE 'V\\_$%' ESCAPE '\\' OR table_name LIKE 'GV\\_$%' ESCAPE '\\')
           UNION ALL
           SELECT table_name, column_name FROM all_tab_columns
            WHERE table_name IN (
              SELECT table_name FROM dictionary
               WHERE table_name LIKE 'DBA_%' OR table_name LIKE 'ALL_%' OR table_name LIKE 'USER_%'
            )
           FETCH FIRST 60000 ROWS ONLY`,
  postgresql: `SELECT table_name, column_name FROM information_schema.columns
                WHERE table_schema IN ('pg_catalog', 'information_schema')
                LIMIT 60000`,
};
// Oracle 내부 표기(V_$xxx/GV_$xxx) → 공개 뷰 이름(V$xxx/GV$xxx). 다른 딕셔너리 뷰는 그대로 통과.
function normalizeOracleTableName(name) {
  if (name.startsWith('GV_$')) return `GV$${name.slice(4)}`;
  if (name.startsWith('V_$')) return `V$${name.slice(3)}`;
  return name;
}

const API_BASE = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/sql-developer\/?$/, '');

/**
 * 연결 하나의 카탈로그 탐색 — 창(아래 DynamicViewExplorerModal)이 연결마다 탭으로 하나씩 띄운다(2026-10-14).
 * 탭마다 전담 세션·즐겨찾기 뷰·결과·조건을 따로 갖고, 다른 탭으로 옮겨도 마운트된 채라 그대로 남는다.
 */
const DynamicViewExplorerBody = ({ isOpen: windowOpen = false, active = true, onClose, connections = [], connectionId, excludeConnIds = [], onConnectionChange }) => {
  const { t } = useTranslation();
  // kind 는 카탈로그 SQL 이 컬럼 값으로 주는 데이터다 — SQL 안의 리터럴은 그대로 두고
  // 화면에 찍을 때만 번역한다. 매칭 안 되는 값은 원문 그대로 보여준다.
  const kindLabel = (k) => ({
    /* eslint-disable-next-line no-restricted-syntax -- 카탈로그 SQL 이 kind 컬럼으로 주는 값. 표시명은 kindLabel() 에서 번역한다 */
    '딕셔너리': t('monitoring:dv.dictionary'),
    /* eslint-disable-next-line no-restricted-syntax -- 카탈로그 SQL 이 kind 컬럼으로 주는 값. 표시명은 kindLabel() 에서 번역한다 */
    '동적뷰': t('monitoring:dv.dynamicView'),
    /* eslint-disable-next-line no-restricted-syntax -- 카탈로그 SQL 이 kind 컬럼으로 주는 값. 표시명은 kindLabel() 에서 번역한다 */
    '동적뷰(V$)': t('monitoring:dv.dynamicViewV'),
    /* eslint-disable-next-line no-restricted-syntax -- 카탈로그 SQL 이 kind 컬럼으로 주는 값. 표시명은 kindLabel() 에서 번역한다 */
    '딕셔너리(information_schema)': t('monitoring:dv.dictionaryIs'),
  }[k] || k);
  const [resultsByTab, setResultsByTab] = useState({});  // { viewName: {rows, columns, executedAt, elapsed} }
  const [errorsByTab, setErrorsByTab] = useState({});
  const [loadingTab, setLoadingTab] = useState(null);
  const [editingQuery, setEditingQuery] = useState(false);
  const [customQuery, setCustomQuery] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [refreshSec, setRefreshSec] = useState(30);
  const [showPicker, setShowPicker] = useState(false);
  // 조건·정렬·인스턴스는 **뷰마다 따로** 기억한다. 탭을 옮겼는데 앞 뷰의 조건이 따라오면
  // 컬럼 이름이 달라 쿼리가 깨지고, 왜 깨졌는지 화면에 단서가 없다.
  const [filtersByTab, setFiltersByTab] = useState({});   // { viewName: [{col, value}] }
  const [sortByTab, setSortByTab] = useState({});         // { viewName: {col, dir} | null }
  const [instByTab, setInstByTab] = useState({});         // { viewName: number | null }
  // 머리글·행번호 고정(바·사) — 더블클릭으로 켜고 끈다
  // 표 — 오라클 세션 분석과 같은 동작(2026-10-03 요청): 머리글 클릭 = 정렬, **우클릭 = 그 열 제자리 고정**,
  // 셀 **더블클릭** = 그 값으로 거르기. 머리글·행번호는 늘 제자리(예전의 더블클릭 고정 토글은 걷었다).
  const [pinnedCols, setPinnedCols] = useState([]);
  const [pinLeft, setPinLeft] = useState({});
  const [colMenu, setColMenu] = useState(null);   // { x, y, col }
  const colMenuRef = useRef(null);
  const thRefs = useRef({});
  const rowNumThRef = useRef(null);
  // 검색 — 받아 온 결과 안에서 모든 열을 본다(대소문자 무시). 셀을 두 번 누르면 그 값이 검색어가 된다(세션 분석과 같다).
  // 쿼리에 조건을 붙이던 동작은 셀 우클릭 메뉴로 옮겼다(서버에서 다시 거르는 것과 화면에서 좁히는 것은 다르다).
  const [gridSearch, setGridSearch] = useState('');
  const deferredSearch = useDeferredValue(gridSearch);   // 칸이 수만 개라 한 글자마다 다시 그리지 않게
  const [cellMenu, setCellMenu] = useState(null);       // { x, y, col, value }
  const cellMenuRef = useRef(null);
  const intervalRef = useRef(null);

  // Oracle + PostgreSQL 연결만 (SQLite builtin 등 제외) — 카탈로그 조회가 이 둘만 지원.
  // **기본은 지금 쓰고 있는 연결이다.** 목록의 첫 번째를 고르던 탓에 교육DB 가 잡혀,
  // 메뉴를 열면 남의 DB 를 조회하고 있었다(사용자 신고).
  const mon = useOwnConnSession({
    isOpen: windowOpen, connections, defaultConnectionId: connectionId, label: 'dbms-catalog',
    filter: ORACLE_OR_PG_CONN,
  });
  // 연결이 정해지기 전에는 조회하지 않는다(2026-10-14) — 연결 없이 나간 첫 조회에 서버가 예시 데이터를 주어,
  // 창을 열면 예시가 먼저 그려지고 실제 값이 그 위를 덮었다. 고를 연결이 아예 없을 때만 그대로 연다(예시 켜기·오류 안내가 보이게)
  const isOpen = windowOpen && (!!mon.connId || mon.conns.length === 0);
  // 탭 이름이 이 연결을 따라가게 창에 알린다(탭 안에서 연결을 바꿀 수 있다)
  useEffect(() => { if (mon.connId && onConnectionChange) onConnectionChange(mon.connId); }, [mon.connId]); // eslint-disable-line react-hooks/exhaustive-deps
  const dynViewConns = mon.conns;
  const selectedConnId = mon.connId;
  const { run, params: monParams } = mon;

  const selectedConn = dynViewConns.find(c => c.id === selectedConnId);
  const dbType = selectedConn?.dbType === 'postgresql' ? 'postgresql' : 'oracle';

  // ===== 카탈로그(딕셔너리 + 동적뷰) — 이 연결이 실제로 갖고 있는 뷰를 그대로 조회 =====
  const [catalogRows, setCatalogRows] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState(null);
  const [catalogSearch, setCatalogSearch] = useState('');
  // 목록이 1,000건을 넘어 키 입력마다 전체를 다시 그리면 한 글자에 0.2~1.4초가 걸린다(실측).
  // 입력값과 '필터에 실제로 쓰는 값' 을 분리해, 타이핑이 멎은 뒤에만 목록을 다시 만든다.
  const [searchDebounced, setSearchDebounced] = useState('');
  useEffect(() => {
    const id = setTimeout(() => setSearchDebounced(catalogSearch), 150);
    return () => clearTimeout(id);
  }, [catalogSearch]);
  // GV$ 와 V$ 가 둘 다 있으면 V$ 는 기본으로 숨긴다(대개 GV$ 가 상위호환 — 단일 인스턴스 조회 시 GV$ 에
  // inst_id 조건만 추가하면 되므로). 체크 해제하면 다시 보인다.
  const [hideDuplicateV, setHideDuplicateV] = useState(true);
  // 뷰 이름 → 컬럼명 목록 — 검색창에서 컬럼명으로도 찾을 수 있도록 카탈로그와 함께 한 번에 가져온다.
  const [columnsByView, setColumnsByView] = useState({});

  const fetchCatalog = useCallback(async () => {
    if (!selectedConnId) { setCatalogRows([]); return; }
    setCatalogLoading(true);
    setCatalogError(null);
    try {
      const res = await run(() => axios.post(`${API_BASE}/sql-developer/execute`, {
        query: CATALOG_QUERY_BY_DBTYPE[dbType],
        ...monParams,
        usrid: 'standalone',
      }));
      const rows = (res.data?.rows || [])
        .map(r => ({
          name: r.NAME ?? r.name,
          kind: r.KIND ?? r.kind,
          description: r.DESCRIPTION ?? r.description ?? null,
        }))
        .filter(r => r.name);
      setCatalogRows(rows);
    } catch (err) {
      setCatalogError(formatApiError(err, t));
      setCatalogRows([]);
    } finally {
      setCatalogLoading(false);
    }
    // 컬럼 메타데이터는 검색 편의용 부가 정보라, 실패해도 뷰 목록 자체는 그대로 쓸 수 있게
    // 별도로 조용히 시도한다(카탈로그 조회 성공/실패와 독립).
    try {
      const colRes = await run(() => axios.post(`${API_BASE}/sql-developer/execute`, {
        query: COLUMN_QUERY_BY_DBTYPE[dbType],
        ...monParams,
        usrid: 'standalone',
      }));
      const map = {};
      (colRes.data?.rows || []).forEach(r => {
        const rawTable = r.TABLE_NAME ?? r.table_name;
        const col = r.COLUMN_NAME ?? r.column_name;
        if (!rawTable || !col) return;
        const viewName = dbType === 'oracle' ? normalizeOracleTableName(rawTable) : rawTable;
        if (!map[viewName]) map[viewName] = [];
        map[viewName].push(col);
      });
      setColumnsByView(map);
    } catch (_err) {
      setColumnsByView({});
    }
    // monParams 가 연결·세션을 품고 있다 — 세션이 정해지면 그 세션으로 다시 훑는다
  }, [run, monParams, selectedConnId, dbType]);

  useEffect(() => {
    if (isOpen && selectedConnId) fetchCatalog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, selectedConnId, dbType]);

  // GV$ 와 짝이 있는 V$ 만 골라 숨김 대상으로 삼는다 — 짝 없는(GV$ 없는) V$ 는 그대로 둔다.
  const dedupedCatalog = useMemo(() => {
    if (dbType !== 'oracle' || !hideDuplicateV) return catalogRows;
    const gvNames = new Set(catalogRows.filter(r => r.name.startsWith('GV$')).map(r => r.name.slice(2)));
    return catalogRows.filter(r => !(r.name.startsWith('V$') && gvNames.has(r.name.slice(1))));
  }, [catalogRows, dbType, hideDuplicateV]);

  // 검색용 소문자 인덱스는 목록/컬럼이 바뀔 때만 만든다. 예전에는 키 입력마다 모든 컬럼에
  // toLowerCase() 를 돌렸다 — 권한 있는 계정이면 컬럼이 6만 건이라 그대로 지연이 된다.
  const searchIndex = useMemo(() => dedupedCatalog.map(r => ({
    row: r,
    hay: (r.name + ' ' + (columnsByView[r.name] || []).join(' ')).toLowerCase(),
  })), [dedupedCatalog, columnsByView]);

  const filteredCatalog = useMemo(() => {
    const q = searchDebounced.trim().toLowerCase();
    if (!q) return dedupedCatalog;
    return searchIndex.filter(e => e.hay.includes(q)).map(e => e.row);
  }, [dedupedCatalog, searchIndex, searchDebounced]);

  // 한 화면에 1,000건 넘게 그리면 DOM 만으로 느려진다. 나머지는 검색으로 좁히게 안내한다.
  const VISIBLE_LIMIT = 200;
  const visibleCatalog = filteredCatalog.length > VISIBLE_LIMIT
    ? filteredCatalog.slice(0, VISIBLE_LIMIT)
    : filteredCatalog;

  // 행마다 만들면 34개 t() 호출 × 행 수가 된다(1,266행이면 4만 회 이상). 한 번만 만든다.
  const curatedDescriptions = useMemo(() => getCuratedDescriptions(t), [t]);
  const favLabels = useMemo(() => ({
    cap: t('monitoring:dv.favLimit', { max: MAX_FAVORITE_VIEWS }),
    remove: t('monitoring:dv.favRemove'),
    add: t('monitoring:dv.favAdd'),
  }), [t]);

  // 즐겨찾기 — dbType 별로 localStorage 에 저장, 다음에 열어도 유지된다. (뷰 이름 문자열 자체가 키)
  const [favoritesByType, setFavoritesByType] = useState(() => {
    const out = {};
    ['oracle', 'postgresql'].forEach(t => {
      try {
        const saved = JSON.parse(localStorage.getItem(favoritesKey(t)) || 'null');
        const list = Array.isArray(saved) && saved.length > 0 ? saved.slice(0, MAX_FAVORITE_VIEWS) : null;
        out[t] = list ? migrateLegacyFavorites(t, list) : DEFAULT_FAVORITES[t];
      } catch { out[t] = DEFAULT_FAVORITES[t]; }
    });
    return out;
  });
  // 매 렌더마다 새 배열이면 이걸 의존성으로 쓰는 메모(행 목록 등)가 전부 무효화된다.
  const favorites = useMemo(() => favoritesByType[dbType] || [], [favoritesByType, dbType]);
  useEffect(() => {
    try { localStorage.setItem(favoritesKey(dbType), JSON.stringify(favorites)); } catch { /* 저장 실패는 무시 */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [favorites, dbType]);

  // useCallback 이어야 아래 행 목록 useMemo 가 매 렌더마다 깨지지 않는다.
  const toggleFavorite = useCallback((name) => {
    setFavoritesByType(prev => {
      const cur = prev[dbType] || [];
      const next = cur.includes(name)
        ? cur.filter(v => v !== name)
        : (cur.length >= MAX_FAVORITE_VIEWS ? cur : [...cur, name]);
      return { ...prev, [dbType]: next };
    });
  }, [dbType]);

  // 행 JSX 를 통째로 메모이즈한다. 검색어를 치는 동안에는(디바운스가 끝나기 전) 목록이
  // 바뀌지 않는데, 메모가 없으면 키 입력마다 200행 × 5엘리먼트를 다시 만들어 0.3초씩 먹었다.
  const catalogRowEls = useMemo(() => (
      visibleCatalog.map(v => {
        const isOn = favorites.includes(v.name);
        const atCap = !isOn && favorites.length >= MAX_FAVORITE_VIEWS;
        const desc = v.description || curatedDescriptions[v.name.toUpperCase()] || genericDescription(v.name, t) || '';
        return (
          <button
            key={v.name}
            type="button"
            className={`dynview-picker-row ${isOn ? 'on' : ''} ${atCap ? 'disabled' : ''}`}
            disabled={atCap}
            title={atCap ? favLabels.cap : (isOn ? favLabels.remove : favLabels.add)}
            onClick={() => toggleFavorite(v.name)}
          >
            <FiStar size={12} className={`dynview-picker-star ${isOn ? 'on' : ''}`} />
            <span className="dynview-picker-name">{v.name}</span>
            <span className="dynview-picker-view">{kindLabel(v.kind)}</span>
            <span className="dynview-picker-desc">{desc}</span>
          </button>
        );
      })
  ), [visibleCatalog, favorites, curatedDescriptions, favLabels, toggleFavorite, t]);

  // 탭 = 즐겨찾기 순서 그대로. 쿼리는 커스텀 저장 없이 매번 "SELECT * FROM <뷰>" 로 생성한다
  // (그대로 보여달라는 요구 — 컬럼을 미리 알 필요 없이 실행 시점에 그리드가 컬럼을 그려낸다).
  const tabs = useMemo(() => favorites.map(name => {
    const meta = catalogRows.find(r => r.name === name);
    // 화면에서 누른 조건·정렬이 **쿼리 본문에 그대로** 나타난다 — 이 화면의 핵심이
    // "무엇을 조회했는지" 를 SQL 로 보여 주는 것이기 때문이다.
    const built = buildCatalogQuery({
      viewName: name,
      filters: filtersByTab[name] || [],
      sort: sortByTab[name] || null,
      instId: instByTab[name] ?? null,
    });
    return {
      name,
      kind: meta?.kind || null,
      description: meta?.description || getCuratedDescriptions(t)[name.toUpperCase()] || genericDescription(name, t),
      query: built || buildDefaultQuery(dbType, name, t),
    };
  }), [favorites, catalogRows, dbType, filtersByTab, sortByTab, instByTab, t]);

  // 탭 바는 즐겨찾기가 몇 개든 항상 5칸 — 모자란 자리는 "+ 추가" 빈 탭으로 채운다.
  // (즐겨찾기 실제 개수/로직은 tabs 그대로 쓰고, 이건 오직 탭 바 렌더링용.)
  const paddedTabs = useMemo(() => {
    const empties = Array.from({ length: Math.max(0, MAX_FAVORITE_VIEWS - tabs.length) }, (_, i) => ({ name: null, empty: true, key: `empty-${i}` }));
    return [...tabs, ...empties];
  }, [tabs]);

  const [activeTabId, setActiveTabId] = useState(null);
  useEffect(() => {
    if (tabs.length === 0) { setActiveTabId(null); return; }
    if (!tabs.some(t => t.name === activeTabId)) setActiveTabId(tabs[0].name);
  }, [tabs, activeTabId]);

  // 인스턴스 리본에 세울 번호 — GV$INSTANCE 를 따로 묻지 않고 **조회 결과의 distinct** 로
  // 판정한다. RAC 여도 한 노드에만 있는 뷰면 리본은 무의미하고, 조회를 한 번 더 하는 것은
  // 이 화면의 취지(누른 것이 곧 SQL)와도 맞지 않는다.
  const instOptions = useMemo(() => {
    const r = activeTabId ? resultsByTab[activeTabId] : null;
    if (!r || !isGlobalView(activeTabId)) return [];
    const col = (r.columns || []).find(c => String(c).toUpperCase() === 'INST_ID');
    if (!col) return [];
    return [...new Set((r.rows || []).map(row => Number(row[col])).filter(Number.isFinite))].sort((a, b) => a - b);
  }, [activeTabId, resultsByTab]);

  const currentTab = tabs.find(t => t.name === activeTabId);
  const effectiveQuery = editingQuery ? customQuery : currentTab?.query;
  const activeFilters = (activeTabId && filtersByTab[activeTabId]) || [];
  const activeInst = (activeTabId && instByTab[activeTabId]) ?? null;


  // 커서 기반 조회 — 한 번에 200건씩 가져오고, 커서를 유지해뒀다가 [다음] 누르면 그 다음 200건을
  // 이어서 더 가져온다(기존 결과에 누적). SQL 자체엔 더 이상 행 제한을 넣지 않는다(buildDefaultQuery 참고) —
  // 안 그러면 DB 쪽 결과집합이 이미 200건으로 잘려 있어 "다음"이 원천적으로 불가능하다.
  const FETCH_SIZE = 200;
  const fetchTabData = useCallback(async (viewName, queryOverride) => {
    if (!selectedConnId) return;
    const tab = tabs.find(t => t.name === viewName);
    const query = queryOverride || tab?.query || buildDefaultQuery(dbType, viewName, t);

    setLoadingTab(viewName);
    setErrorsByTab(prev => ({ ...prev, [viewName]: null }));
    const startTime = performance.now();

    try {
      const res = await run(() => axios.post(`${API_BASE}/sql-developer/fetch-data`, {
        query,
        ...monParams,
        fetchSize: FETCH_SIZE,
        usrid: 'standalone',
      }));

      const elapsed = performance.now() - startTime;
      const rows = res.data?.rows || [];
      const columns = rows.length > 0 ? Object.keys(rows[0]) : [];

      setResultsByTab(prev => ({
        ...prev,
        [viewName]: {
          rows, columns, executedAt: new Date(), elapsed: Math.round(elapsed),
          cursorId: res.data?.cursorId || null,
          hasMore: !!res.data?.hasMore,
        },
      }));
    } catch (err) {
      const msg = formatApiError(err, t);
      setErrorsByTab(prev => ({ ...prev, [viewName]: msg }));
    } finally {
      setLoadingTab(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConnId, dbType, tabs]);

  // [다음] — 같은 커서로 다음 배치를 더 가져와 기존 행 뒤에 이어붙인다.
  const [loadingMoreTab, setLoadingMoreTab] = useState(null);
  const handleFetchMore = useCallback(async (viewName) => {
    const cur = resultsByTab[viewName];
    if (!cur || !cur.hasMore || !cur.cursorId) return;
    setLoadingMoreTab(viewName);
    try {
      // 커서 이어받기는 커서 캐시가 connection 을 들고 있다 — 세션을 빌리지 않는다
      const res = await run(() => axios.post(`${API_BASE}/sql-developer/fetch-data`, {
        cursorId: cur.cursorId,
        fetchSize: FETCH_SIZE,
        usrid: 'standalone',
      }));
      const newRows = res.data?.rows || [];
      setResultsByTab(prev => {
        const existing = prev[viewName];
        if (!existing) return prev;
        const mergedRows = [...existing.rows, ...newRows];
        return {
          ...prev,
          [viewName]: {
            ...existing,
            rows: mergedRows,
            columns: existing.columns.length > 0 ? existing.columns : (mergedRows.length > 0 ? Object.keys(mergedRows[0]) : []),
            cursorId: res.data?.cursorId || null,
            hasMore: !!res.data?.hasMore,
          },
        };
      });
    } catch (err) {
      setErrorsByTab(prev => ({ ...prev, [viewName]: formatApiError(err, t) }));
    } finally {
      setLoadingMoreTab(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resultsByTab]);

  // 탭 변경 시 자동 조회 (결과 없을 때)
  useEffect(() => {
    if (!isOpen || !selectedConnId || !activeTabId) return;
    setEditingQuery(false);
    if (!resultsByTab[activeTabId] && loadingTab !== activeTabId) {
      fetchTabData(activeTabId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, activeTabId, selectedConnId]);

  // 연결(또는 dbType) 변경 시 기존 결과 초기화 — 다른 DBMS 로 넘어가면 이전 결과는 의미가 없다.
  useEffect(() => {
    if (selectedConnId) {
      setResultsByTab({});
      setErrorsByTab({});
    }
  }, [selectedConnId, dbType]);

  // 자동 갱신
  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (autoRefresh && isOpen && selectedConnId && activeTabId) {
      intervalRef.current = setInterval(() => {
        fetchTabData(activeTabId);
      }, refreshSec * 1000);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [autoRefresh, isOpen, selectedConnId, activeTabId, refreshSec, fetchTabData]);

  // 조건·정렬을 바꾸면 **바로 다시 조회한다.** 누르고 나서 또 [실행] 을 눌러야 한다면
  // 값을 눌러 거른다는 동작 자체가 성립하지 않는다.
  const applyAndRun = useCallback((viewName, nextFilters, nextSortVal, nextInst) => {
    const query = buildCatalogQuery({
      viewName,
      filters: nextFilters,
      sort: nextSortVal,
      instId: nextInst,
    });
    if (query) fetchTabData(viewName, query);
  }, [fetchTabData]);

  const handleCellFilter = useCallback((col, value) => {
    if (!activeTabId || editingQuery) return;
    const cur = filtersByTab[activeTabId] || [];
    const next = addFilter(cur, col, value);
    if (next === cur) return;
    setFiltersByTab(prev => ({ ...prev, [activeTabId]: next }));
    applyAndRun(activeTabId, next, sortByTab[activeTabId] || null, instByTab[activeTabId] ?? null);
  }, [activeTabId, editingQuery, filtersByTab, sortByTab, instByTab, applyAndRun]);

  const handleRemoveFilter = useCallback((idx) => {
    if (!activeTabId) return;
    const cur = filtersByTab[activeTabId] || [];
    const next = cur.filter((_, i) => i !== idx);
    setFiltersByTab(prev => ({ ...prev, [activeTabId]: next }));
    applyAndRun(activeTabId, next, sortByTab[activeTabId] || null, instByTab[activeTabId] ?? null);
  }, [activeTabId, filtersByTab, sortByTab, instByTab, applyAndRun]);

  const handleSortColumn = useCallback((col) => {
    if (!activeTabId || editingQuery) return;
    const next = nextSort(sortByTab[activeTabId] || null, col);
    setSortByTab(prev => ({ ...prev, [activeTabId]: next }));
    applyAndRun(activeTabId, filtersByTab[activeTabId] || [], next, instByTab[activeTabId] ?? null);
  }, [activeTabId, editingQuery, sortByTab, filtersByTab, instByTab, applyAndRun]);

  const handleInstFilter = useCallback((inst) => {
    if (!activeTabId) return;
    // 같은 노드를 다시 누르면 해제 — 리본은 토글이다
    const cur = instByTab[activeTabId] ?? null;
    const next = (cur === inst) ? null : inst;
    setInstByTab(prev => ({ ...prev, [activeTabId]: next }));
    applyAndRun(activeTabId, filtersByTab[activeTabId] || [], sortByTab[activeTabId] || null, next);
  }, [activeTabId, instByTab, filtersByTab, sortByTab, applyAndRun]);

  const handleExecute = () => {
    if (activeTabId) fetchTabData(activeTabId, effectiveQuery);
  };

  const handleResetQuery = () => {
    setCustomQuery(currentTab?.query || '');
    setEditingQuery(false);
  };

  const handleEditToggle = () => {
    if (!editingQuery) setCustomQuery(currentTab?.query || '');
    setEditingQuery(!editingQuery);
  };

  // 훅은 조기 return 위에 있어야 한다(rules-of-hooks). result 파생도 함께 올린다.
  const result = activeTabId ? resultsByTab[activeTabId] : null;

  // 결과 그리드는 컬럼이 100개를 넘는 V$ 뷰가 흔해 td 가 2만 개를 넘는다(실측 22,600).
  // 검색어를 한 글자 칠 때마다 이걸 다시 만들면 그 자체로 0.3초가 걸린다 — result 가
  // 바뀔 때만 만들도록 떼어낸다.
  const gridEl = useMemo(() => {
    if (!result || result.rows.length === 0) return null;
    const q = deferredSearch.trim().toLowerCase();
    const cellText = (v) => (v === null || v === undefined ? '' : typeof v === 'object' ? JSON.stringify(v) : String(v));
    // 원래 행번호를 지킨다 — 걸러도 「몇 번째 줄이었나」 가 남아야 한다
    const shown = result.rows.map((row, n) => ({ row, n }))
      .filter(({ row }) => !q || result.columns.some(c => cellText(row[c]).toLowerCase().includes(q)));
    const sortState = (activeTabId && sortByTab[activeTabId]) || null;
    const clickable = !editingQuery;      // 직접 편집 중에는 손대지 않는다(그 사람의 SQL 이 이긴다)
    const lastPinned = [...result.columns].reverse().find(c => pinnedCols.includes(c)) || null;
    const pinClass = (col) => (pinnedCols.includes(col) ? ` dynview-pin${col === lastPinned ? ' dynview-pin-last' : ''}` : '');
    const pinStyle = (col) => (pinnedCols.includes(col) && pinLeft[col] != null ? { left: pinLeft[col] } : undefined);
    return (
      <table className="dynview-grid">
        <thead>
          <tr>
            <th className="dynview-rownum" ref={rowNumThRef}>#</th>
            {result.columns.map(col => {
              const dir = sortState && sortState.col === col ? sortState.dir : null;
              return (
                <th
                  key={col}
                  ref={(el) => { thRefs.current[col] = el; }}
                  className={`${clickable ? 'dynview-th-sortable' : ''}${dir ? ` dynview-th-sorted-${dir}` : ''}${pinClass(col)}`}
                  style={pinStyle(col)}
                  onClick={clickable ? () => handleSortColumn(col) : undefined}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    setColMenu({ ...menuPoint(e, 200, 120, e.view || window), col });
                  }}
                  title={t('monitoring:dv.sortHint')}
                  aria-sort={dir ? (dir === 'asc' ? 'ascending' : 'descending') : undefined}
                >
                  {col}
                  {dir && <i className="dynview-sort-mark" aria-hidden="true">{dir === 'asc' ? '▲' : '▼'}</i>}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {shown.map(({ row, n }) => (
            <tr key={n}>
              <td className="dynview-rownum">{n + 1}</td>
              {result.columns.map(col => {
                const v = row[col];
                const display = v === null || v === undefined
                  ? <span className="dynview-null">(null)</span>
                  : typeof v === 'object'
                    ? JSON.stringify(v)
                    : String(v);
                return (
                  <td
                    key={col}
                    className={`dynview-td-filterable${q && cellText(v).toLowerCase() === q ? ' dynview-td-searching' : ''}${pinClass(col)}`}
                    style={pinStyle(col)}
                    /* 값을 **두 번** 누르면 그 값이 검색어가 된다(한 번 누르기는 값을 읽으려는 것이다). 두 번 누를 때 글자가
                       선택되지 않게 막는다. 우클릭 = 그 값으로 쿼리에 조건 추가(서버에서 다시 거른다) */
                    onMouseDown={(e) => { if (e.detail > 1) e.preventDefault(); }}
                    onDoubleClick={() => setGridSearch(cellText(v))}
                    onContextMenu={clickable && typeof v !== 'object' ? (e) => {
                      e.preventDefault();
                      setCellMenu({ ...menuPoint(e, 240, 80, e.view || window), col, value: v });
                    } : undefined}
                    title={t('monitoring:dv.filterHint')}
                  >
                    {display}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    );
  }, [result, activeTabId, sortByTab, editingQuery, pinnedCols, pinLeft, deferredSearch, handleSortColumn, t]);

  // 보이는 줄 수(검색 중이면 「보이는 / 전체」)
  const shownCount = useMemo(() => {
    if (!result) return 0;
    const q = deferredSearch.trim().toLowerCase();
    if (!q) return result.rows.length;
    const txt = (v) => (v === null || v === undefined ? '' : typeof v === 'object' ? JSON.stringify(v) : String(v)).toLowerCase();
    return result.rows.filter(row => result.columns.some(c => txt(row[c]).includes(q))).length;
  }, [result, deferredSearch]);
  // 탭을 바꾸면 검색어는 비운다 — 다른 뷰의 열에는 뜻이 없다
  useEffect(() => { setGridSearch(''); }, [activeTabId]);
  // 셀 우클릭 메뉴 — 바깥을 누르거나 Esc 면 닫는다
  useEffect(() => {
    if (!cellMenu) return undefined;
    const onDown = (e) => { if (cellMenuRef.current && !cellMenuRef.current.contains(e.target)) setCellMenu(null); };
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); setCellMenu(null); } };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey, true);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey, true); };
  }, [cellMenu]);

  // 결과 열이 바뀌면(탭·쿼리) 없는 열의 고정은 버린다
  useEffect(() => {
    if (result) setPinnedCols(prev => prunePinned(prev, result.columns));
  }, [result]);
  // 제자리 고정 — 보이는 순서대로 앞의 고정 열 폭(그려진 머리글을 잰 값)만큼, 행번호 칸 뒤에서부터 띄운다(utils/gridPin)
  useLayoutEffect(() => {
    if (!result) return;
    const start = (rowNumThRef.current && rowNumThRef.current.offsetWidth) || 0;
    const { left } = pinnedLeftOffsets(result.columns, pinnedCols,
      (k) => (thRefs.current[k] && thRefs.current[k].offsetWidth) || 0, start);
    setPinLeft(prev => (JSON.stringify(prev) === JSON.stringify(left) ? prev : left));
  }, [pinnedCols, result]);
  // 머리글 우클릭 메뉴 — 바깥을 누르거나 Esc 면 닫는다
  useEffect(() => {
    if (!colMenu) return undefined;
    const onDown = (e) => { if (colMenuRef.current && !colMenuRef.current.contains(e.target)) setColMenu(null); };
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); setColMenu(null); } };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey, true);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey, true); };
  }, [colMenu]);

  if (!isOpen) return null;

  const error = activeTabId ? errorsByTab[activeTabId] : null;
  const isLoading = activeTabId && loadingTab === activeTabId;

  const picker = showPicker && (
    <div className="dynview-picker-overlay" onClick={() => setShowPicker(false)}>
      <div className="app-modal dynview-picker-panel" data-app-modal onClick={(e) => e.stopPropagation()}>
        <div className="app-modal-header dynview-picker-header">
          <span className="app-modal-title">
            {dbType === 'postgresql' ? 'PostgreSQL' : 'Oracle'} {t('monitoring:dv.dictAndViews')}
            <span className="dynview-picker-count">
              {t('monitoring:dv.favSummary', { n: favorites.length, max: MAX_FAVORITE_VIEWS, total: catalogRows.length })}
            </span>
          </span>
          <button type="button" className="app-modal-close dynview-close" onClick={() => setShowPicker(false)}>
            <FiX size={16} />
          </button>
        </div>
        <div className="dynview-picker-search">
          <FiSearch size={13} />
          <input
            type="text"
            placeholder={t('monitoring:dv.searchPlaceholder')}
            value={catalogSearch}
            onChange={(e) => setCatalogSearch(e.target.value)}
            autoFocus
          />
          <button
            type="button"
            className="dynview-picker-refresh"
            onClick={fetchCatalog}
            disabled={catalogLoading}
            title={t('monitoring:dv.refreshCatalog')}
          >
            <FiRefreshCw size={12} className={catalogLoading ? 'dynview-spin' : ''} />
          </button>
        </div>
        {dbType === 'oracle' && (
          <label className="dynview-hide-v-toggle">
            <input type="checkbox" checked={hideDuplicateV} onChange={(e) => setHideDuplicateV(e.target.checked)} />
            {t('monitoring:dv.hideDuplicateV')}
          </label>
        )}
        <div className="dynview-picker-body">
          {catalogLoading && catalogRows.length === 0 && (
            <div className="dynview-picker-status">{t('monitoring:dv.loadingCatalog')}</div>
          )}
          {catalogError && (
            <div className="dynview-picker-status error">{t('monitoring:dv.catalogError', { message: catalogError })}</div>
          )}
          {!catalogLoading && !catalogError && filteredCatalog.length === 0 && (
            <div className="dynview-picker-status">
              {catalogRows.length === 0 ? t('monitoring:dv.noAccessible') : t('monitoring:dv.noSearchResult')}
            </div>
          )}
          {catalogRowEls}
          {filteredCatalog.length > VISIBLE_LIMIT && (
            <div className="dynview-picker-status">
              {t('monitoring:dv.tooMany', { shown: VISIBLE_LIMIT, total: filteredCatalog.length })}
            </div>
          )}
        </div>
        <div className="dynview-picker-footnote">
          {t('monitoring:dv.favScope')}
          {t('monitoring:dv.catalogScope')}
          PostgreSQL: pg_catalog.pg_views + information_schema.views).
        </div>
      </div>
    </div>
  );

  return (
    <ConnTabPane active={active} toolbar={<>
        {/* 이 탭의 연결·세션 — 다른 탭이 연 연결은 고를 수 없다(같은 전담 세션을 두 탭이 함께 쓰면 문장이 섞인다) */}
        <>
          <OwnConnTarget hideConn
            conns={mon.conns.filter(c => c.id === mon.connId || !excludeConnIds.includes(c.id))} connId={mon.connId} onConnChange={mon.setConnId}
            sessionId={mon.sessionId} session={mon.session}
            sessionError={mon.sessionError} creating={mon.creating}
            onPickSession={mon.pickSession}
          />
          <button type="button" className="app-btn dynview-picker-toggle" onClick={() => setShowPicker(true)}>
            <FiGrid size={13} /> {t('monitoring:dv.pickViews')}
          </button>
        </>
      </>}>

        {/* Tabs — 즐겨찾기가 몇 개든 항상 5칸. 빈 자리는 "+ 추가" 로 표시하고 누르면 뷰 선택을 연다. */}
        <div className="dynview-tabs" role="tablist">
          {paddedTabs.map(tab => (
            tab.empty ? (
              <button
                key={tab.key}
                type="button"
                className="dynview-tab dynview-tab-empty-slot"
                onClick={() => setShowPicker(true)}
              >
                + {t('monitoring:dv.pickViews')}
              </button>
            ) : (
              <button
                key={tab.name}
                type="button"
                role="tab"
                aria-selected={activeTabId === tab.name}
                className={`dynview-tab ${activeTabId === tab.name ? 'active' : ''}`}
                onClick={() => setActiveTabId(tab.name)}
              >
                {tab.name}
                {tab.kind && <span className="dynview-tab-view">{kindLabel(tab.kind)}</span>}
              </button>
            )
          ))}
        </div>

        {/* Body */}
        {tabs.length === 0 ? (
          <div className="dynview-tab-empty">
            <FiGrid size={32} />
            <span>{t('monitoring:dv.noFavorites')}</span>
          </div>
        ) : (
          <div className="dynview-body">
            {currentTab?.description && <div className="dynview-tab-desc">{currentTab.description}</div>}

            {/* 조건·인스턴스 리본 — 누른 것이 무엇인지 한 줄로 보이고, x 로 떼어낸다.
                직접 편집 중에는 손대지 않는다(그 사람이 쓴 SQL 이 이긴다) — 그 사실을 말한다. */}
            {(activeFilters.length > 0 || instOptions.length > 1 || editingQuery) && (
              <div className="dynview-filter-bar">
                {instOptions.length > 1 && (
                  <>
                    <span className="dynview-filter-label">{t('monitoring:dv.instance')}</span>
                    {instOptions.map(n => (
                      <button
                        key={n}
                        type="button"
                        className={`dynview-inst-chip ${activeInst === n ? 'active' : ''}`}
                        onClick={() => handleInstFilter(n)}
                        disabled={editingQuery}
                      >
                        {n}
                      </button>
                    ))}
                  </>
                )}
                {activeFilters.map((f, i) => (
                  <span key={`${f.col}-${i}`} className="dynview-filter-chip">
                    <b>{f.col}</b>
                    <code>{f.value === null || f.value === undefined ? 'NULL' : String(f.value)}</code>
                    <button
                      type="button"
                      onClick={() => handleRemoveFilter(i)}
                      aria-label={t('monitoring:dv.removeFilter')}
                      disabled={editingQuery}
                    >
                      <FiX size={11} />
                    </button>
                  </span>
                ))}
                {editingQuery && (
                  <span className="dynview-filter-note">{t('monitoring:dv.editingNote')}</span>
                )}
              </div>
            )}

            {/* Query viewer/editor */}
            <div className="dynview-query-section">
              <div className="dynview-query-header">
                <span className="dynview-query-label">{t('monitoring:dv.query')}</span>
                <div className="dynview-query-actions">
                  <button className="dynview-btn-secondary" onClick={handleEditToggle}>
                    {editingQuery ? t('monitoring:dv.viewMode') : t('monitoring:dv.editMode')}
                  </button>
                  {editingQuery && (
                    <button className="dynview-btn-secondary" onClick={handleResetQuery}>
                      {t('monitoring:dv.resetQuery')}
                    </button>
                  )}
                  <button
                    className="dynview-btn-primary"
                    onClick={handleExecute}
                    disabled={isLoading || !selectedConnId}
                  >
                    <FiPlay size={12} /> {t('monitoring:dv.run')}
                  </button>
                </div>
              </div>
              <textarea
                className="dynview-query-textarea"
                value={effectiveQuery || ''}
                onChange={(e) => setCustomQuery(e.target.value)}
                readOnly={!editingQuery}
                spellCheck={false}
              />
            </div>

            {/* Status bar */}
            <div className="dynview-status">
              <div className="dynview-status-left">
                {isLoading && <span className="dynview-loading">{t('monitoring:dv.loading')}</span>}
                {!isLoading && result && (
                  <>
                    <span>
                      {deferredSearch.trim()
                        ? t('monitoring:dv.shownOf', { shown: shownCount, total: result.rows.length })
                        : `${result.rows.length}${t('monitoring:dv.rows')}`}
                    </span>
                    <span className="dynview-status-sep">·</span>
                    <span>{result.elapsed}ms</span>
                    <span className="dynview-status-sep">·</span>
                    <span>{formatTime(result.executedAt)}</span>
                    {result.hasMore && (
                      <button
                        type="button"
                        className="dynview-fetchmore-btn"
                        onClick={() => handleFetchMore(activeTabId)}
                        disabled={loadingMoreTab === activeTabId}
                      >
                        {loadingMoreTab === activeTabId
                          ? t('monitoring:dv.loadingMore')
                          : t('monitoring:dv.fetchMore', { n: FETCH_SIZE })}
                      </button>
                    )}
                  </>
                )}
                {!isLoading && !result && !error && <span className="dynview-status-empty">{t('monitoring:dv.runQueryFirst')}</span>}
              </div>
              <div className="dynview-status-right">
                {/* 검색 — 받아 온 결과 안에서 모든 열(세션 분석과 같은 꼴). Esc 로도 지운다(창을 닫는 Esc 보다 먼저) */}
                <div className="dynview-search">
                  <FiSearch size={12} />
                  <input
                    type="text"
                    placeholder={t('monitoring:dv.searchRows')}
                    value={gridSearch}
                    onChange={(e) => setGridSearch(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Escape' && gridSearch) { e.stopPropagation(); setGridSearch(''); } }}
                    disabled={!result}
                  />
                  {gridSearch && (
                    <button type="button" className="dynview-search-clear" onClick={() => setGridSearch('')}
                      title={t('monitoring:dv.clearSearch')} aria-label={t('monitoring:dv.clearSearch')}>
                      <FiX size={12} />
                    </button>
                  )}
                </div>
                <label className="dynview-autorefresh">
                  <input
                    type="checkbox"
                    checked={autoRefresh}
                    onChange={(e) => setAutoRefresh(e.target.checked)}
                  />
                  {t('monitoring:dv.autoRefresh')}
                </label>
                {autoRefresh && (
                  <select
                    className="dynview-select-inline"
                    value={refreshSec}
                    onChange={(e) => setRefreshSec(parseInt(e.target.value, 10))}
                  >
                    <option value={10}>{t('monitoring:dv.every.sec10')}</option>
                    <option value={30}>{t('monitoring:dv.every.sec30')}</option>
                    <option value={60}>{t('monitoring:dv.every.min1')}</option>
                  </select>
                )}
                <button
                  className="dynview-refresh-btn"
                  onClick={() => fetchTabData(activeTabId)}
                  disabled={isLoading || !selectedConnId}
                  title={t('monitoring:dv.refresh')}
                >
                  <FiRefreshCw size={14} className={isLoading ? 'dynview-spin' : ''} />
                </button>
              </div>
            </div>

            {/* Error */}
            {error && (
              <div className="dynview-error">
                <strong>{t('monitoring:dv.queryFailed')}</strong> {error}
              </div>
            )}

            {/* Result grid */}
            <div className="dynview-grid-container">
              {gridEl}
              {result && result.rows.length === 0 && !error && (
                <div className="dynview-empty">{t('monitoring:dv.noRows')}</div>
              )}
              {result && result.rows.length > 0 && shownCount === 0 && (
                <div className="dynview-empty">{t('monitoring:dv.noSearchMatch', { q: deferredSearch.trim() })}</div>
              )}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="dynview-footer">
          <div className="dynview-footer-note">
            <FiInfo size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} />
            {t('monitoring:dv.needSelectPriv', { name: currentTab?.name || t('monitoring:dv.selectedView') })}
          </div>
          <button className="app-btn dynview-btn-secondary" onClick={onClose}>{t('monitoring:dv.close')}</button>
        </div>
      {picker}
      {cellMenu && (
        <div ref={cellMenuRef} className="rg-header-menu" style={{ left: cellMenu.x, top: cellMenu.y }} role="menu">
          <button type="button" role="menuitem" onClick={() => { setGridSearch(String(cellMenu.value ?? '')); setCellMenu(null); }}>
            {t('monitoring:dv.menuSearch')}
          </button>
          <button type="button" role="menuitem" onClick={() => { handleCellFilter(cellMenu.col, cellMenu.value); setCellMenu(null); }}>
            {t('monitoring:dv.menuAddCondition')}
          </button>
        </div>
      )}
      {colMenu && (
        <div ref={colMenuRef} className="rg-header-menu" style={{ left: colMenu.x, top: colMenu.y }} role="menu">
          <button type="button" role="menuitem" onClick={() => {
            const k = colMenu.col;
            setPinnedCols(prev => (prev.includes(k) ? prev.filter(x => x !== k) : [...prev, k]));
            setColMenu(null);
          }}>
            {pinnedCols.includes(colMenu.col) ? t('common:rg.unpinCol') : t('common:rg.pinCol')}
          </button>
          <button type="button" role="menuitem" disabled={pinnedCols.length === 0} onClick={() => { setPinnedCols([]); setColMenu(null); }}>
            {t('common:rg.unpinAllCols')}
          </button>
        </div>
      )}
    </ConnTabPane>
  );
};

/**
 * DBMS 카탈로그 창 — **DB 연결마다 탭**(2026-10-14 요청: 여러 DB 를 동시에 조회). 틀은 오라클 전문가 창들과 같은 ConnTabsWindow.
 */
const DynamicViewExplorerModal = (props) => {
  const { t } = useTranslation();
  return (
    <ConnTabsWindow sessionLabel="dbms-catalog"
      isOpen={props.isOpen} onClose={props.onClose} connections={props.connections}
      defaultConnectionId={props.defaultConnectionId} filter={ORACLE_OR_PG_CONN}
      icon={<FiDatabase size={14} />} title={t('monitoring:dv.title')}
      overlayClassName="dynview-overlay" className="dynview-modal" headerClassName="dynview-header" closeClassName="dynview-close"
      renderBody={(tab) => <DynamicViewExplorerBody {...props} connectionId={tab.defaultConnectionId} active={tab.active} excludeConnIds={tab.excludeConnIds} onConnectionChange={tab.onConnectionChange} />}
    />
  );
};

// 작업 탭을 오갈 때 Header 가 새 콜백을 넘겨도 본문을 다시 그리지 않는다(common/memoWindow)
export default memoWindow(DynamicViewExplorerModal);
